/**
 * GameLayout Component
 * Unified layout wrapper for all games with consistent structure
 */

import React from 'react';
import { GAME_CONFIG } from '../config/gameConfig';

const GameLayout = ({
  title,
  children,
  score,
  level,
  gameState,
  onPause,
  onResume,
  onRestart,
  onHome,
  showControls = true,
  controlsContent,
}) => {
  return (
    <div className="game-layout" style={styles.container}>
      {/* Header with Title and Score */}
      <header style={styles.header} className="game-header">
        <div className="d-flex justify-content-between align-items-center w-100">
          <div>
            <h1 style={styles.title} className="mb-0">{title}</h1>
            {level && (
              <p style={styles.subtitle} className="mb-0">Level: {level}</p>
            )}
          </div>
          <div className="text-end">
            <div style={styles.scoreText}>
              Score: <strong>{score || 0}</strong>
            </div>
            <div style={styles.stateText}>
              {gameState === 'paused' && '⏸ PAUSED'}
              {gameState === 'gameOver' && '💀 GAME OVER'}
              {gameState === 'win' && '🎉 WIN'}
            </div>
          </div>
        </div>
      </header>

      {/* Game Content Area */}
      <main style={styles.content} className="game-content">
        {children}
      </main>

      {/* Controls */}
      {showControls && (
        <footer style={styles.controls} className="game-controls">
          <div className="d-flex gap-2 justify-content-center flex-wrap">
            {gameState === 'playing' && onPause && (
              <button
                className="btn btn-warning btn-sm"
                onClick={onPause}
                aria-label="Pause game"
              >
                ⏸ Pause
              </button>
            )}
            {gameState === 'paused' && onResume && (
              <button
                className="btn btn-success btn-sm"
                onClick={onResume}
                aria-label="Resume game"
              >
                ▶ Resume
              </button>
            )}
            {(gameState === 'gameOver' || gameState === 'win') && onRestart && (
              <button
                className="btn btn-primary btn-sm"
                onClick={onRestart}
                aria-label="Restart game"
              >
                🔄 Restart
              </button>
            )}
            {onHome && (
              <button
                className="btn btn-secondary btn-sm"
                onClick={onHome}
                aria-label="Go back to games menu"
              >
                🏠 Home
              </button>
            )}
          </div>
          {controlsContent && (
            <div style={styles.customControls}>
              {controlsContent}
            </div>
          )}
        </footer>
      )}

      <style>{`
        .game-layout {
          background: linear-gradient(135deg, ${GAME_CONFIG.colors.dark} 0%, ${GAME_CONFIG.colors.darker} 100%);
          border-radius: 12px;
          overflow: hidden;
          box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.3);
        }

        .game-header {
          background: rgba(0, 0, 0, 0.3);
          padding: 1rem;
          border-bottom: 2px solid ${GAME_CONFIG.colors.primary};
        }

        .game-content {
          display: flex;
          align-items: center;
          justify-content: center;
          min-height: 400px;
          padding: 1rem;
        }

        .game-controls {
          background: rgba(0, 0, 0, 0.3);
          padding: 1rem;
          border-top: 2px solid ${GAME_CONFIG.colors.primary};
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }

        @media (max-width: 768px) {
          .game-content {
            min-height: 300px;
          }

          .game-header h1 {
            font-size: 1.5rem;
          }
        }
      `}</style>
    </div>
  );
};

const styles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    minHeight: '500px',
  },
  header: {
    padding: '1rem',
    backgroundColor: 'rgba(0, 0, 0, 0.3)',
    borderBottom: `2px solid ${GAME_CONFIG.colors.primary}`,
  },
  title: {
    color: GAME_CONFIG.colors.white,
    fontSize: '2rem',
    fontWeight: 'bold',
    margin: 0,
  },
  subtitle: {
    color: GAME_CONFIG.colors.light,
    fontSize: '0.9rem',
    margin: '0.25rem 0 0 0',
  },
  scoreText: {
    color: GAME_CONFIG.colors.accent,
    fontSize: '1.1rem',
    fontWeight: 'bold',
  },
  stateText: {
    color: GAME_CONFIG.colors.accent2,
    fontSize: '0.9rem',
    fontWeight: 'bold',
    marginTop: '0.25rem',
  },
  content: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '1rem',
    minHeight: '300px',
  },
  controls: {
    padding: '1rem',
    backgroundColor: 'rgba(0, 0, 0, 0.3)',
    borderTop: `2px solid ${GAME_CONFIG.colors.primary}`,
  },
  customControls: {
    marginTop: '0.5rem',
    display: 'flex',
    gap: '0.5rem',
    justifyContent: 'center',
    flexWrap: 'wrap',
  },
};

export default GameLayout;
